import { useEffect, useId, useRef, type ReactNode } from 'react';
import { CloseIcon } from './icons';

export type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  className?: string;
  rootClassName?: string;
  showClose?: boolean;
  maxHeight?: string;
  persistent?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
};

export function BottomSheet({
  open,
  onClose,
  children,
  title,
  className,
  rootClassName,
  showClose = true,
  maxHeight = '82dvh',
  persistent = false,
  expanded = true,
  onToggle,
}: BottomSheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open || persistent) return undefined;

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => panelRef.current?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(
        'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ));

      if (!focusable.length) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose, open, persistent]);

  if (!open) return null;

  const rootClasses = [
    'hopper-bottom-sheet',
    persistent ? 'hopper-bottom-sheet--persistent' : '',
    persistent ? (expanded ? 'is-expanded' : 'is-collapsed') : '',
    rootClassName,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClasses} data-state="open" data-expanded={persistent ? String(expanded) : undefined}>
      {!persistent && (
        <button className="hopper-bottom-sheet__backdrop" type="button" onClick={onClose} aria-label="Закрыть" />
      )}
      <section
        ref={panelRef}
        tabIndex={persistent ? undefined : -1}
        className={['hopper-bottom-sheet__panel', className].filter(Boolean).join(' ')}
        role={persistent ? 'region' : 'dialog'}
        aria-modal={persistent ? undefined : true}
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : persistent ? 'Карточка места' : 'Панель'}
        style={{ maxHeight }}
      >
        {persistent && onToggle ? (
          <button
            className="hopper-bottom-sheet__handle-button"
            type="button"
            onClick={onToggle}
            aria-label={expanded ? 'Свернуть карточку' : 'Развернуть карточку'}
          >
            <span className="hopper-bottom-sheet__handle" aria-hidden="true" />
          </button>
        ) : (
          <div className="hopper-bottom-sheet__handle" aria-hidden="true" />
        )}
        {(title || showClose) && (
          <header className="hopper-bottom-sheet__header">
            {title ? <h2 id={titleId}>{title}</h2> : <span />}
            {showClose && (
              <button className="hopper-bottom-sheet__close" type="button" onClick={onClose} aria-label="Закрыть">
                <CloseIcon size={20} />
              </button>
            )}
          </header>
        )}
        <div className="hopper-bottom-sheet__content">{children}</div>
      </section>
    </div>
  );
}
